/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useState } from 'react';

import { defineMessage, useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { Flex } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { ActionCardFooter } from '../../common/ui/action-card-wrapper';

import type { CapacityPlanningAction, CapacityPlanningViewType } from './types';

const dismissButtonMessage = defineMessage({
	id: 'capacity-planning.dismiss-button.non-final',
	defaultMessage: 'Dismiss',
	description:
		'Button label for dismissing the capacity planning people view rovo chat card without applying changes.',
});
const acceptAllButtonMessage = defineMessage({
	id: 'capacity-planning.accept-all-button.non-final',
	defaultMessage: 'Accept all',
	description:
		'Button label for accepting all changes in the capacity planning people view rovo chat card.',
});
const acceptSelectedButtonMessage = defineMessage({
	id: 'capacity-planning.accept-selected-button.non-final',
	defaultMessage: 'Accept selected',
	description:
		'Button label when one or more items are selected to be accepted in the capacity planning people view.',
});
const dismissSelectedButtonMessage = defineMessage({
	id: 'capacity-planning.dismiss-selected-button.non-final',
	defaultMessage: 'Dismiss selected',
	description:
		'Button label when one or more items are selectedto be dismissed in the capacity planning people view.',
});

const footerStyles = cssMap({
	wrapper: {
		gap: token('space.050'),
		justifyContent: 'flex-end',
	},
});

type CapacityPlanningCardFooterProps = {
	displayedActions: CapacityPlanningAction[];
	selectedKeys: Set<string>;
	getStatus: (action: CapacityPlanningAction) => string;
	onConfirm: (action: CapacityPlanningAction) => Promise<void>;
	onCancel: (action: CapacityPlanningAction) => Promise<void>;
	onClearSelection: () => void;
	allActionsApplied: boolean;
	viewType: CapacityPlanningViewType;
};

function isActionIdle(
	action: CapacityPlanningAction,
	getStatus: (a: CapacityPlanningAction) => string,
): boolean {
	return action.status === 'planned' && getStatus(action) === 'idle';
}

export function CapacityPlanningCardFooter({
	displayedActions,
	selectedKeys,
	getStatus,
	onConfirm,
	onCancel,
	onClearSelection,
	allActionsApplied,
	viewType,
}: CapacityPlanningCardFooterProps): JSX.Element | null {
	di(useAnalytics);
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const [isBulkActioning, setIsBulkActioning] = useState(false);
	const idleActions = displayedActions.filter((action) => isActionIdle(action, getStatus));
	const selectedActions = displayedActions.filter((action) =>
		selectedKeys.has(action.invocationId),
	);
	const selectedCount = selectedKeys.size;

	const getActionTypeCounts = useCallback(
		(actions: CapacityPlanningAction[]) =>
			actions.reduce<Record<string, number>>((acc, action) => {
				const actionType = action.data.action;
				acc[actionType] = (acc[actionType] ?? 0) + 1;
				return acc;
			}, {}),
		[],
	);

	const fireAnalytics = useCallback(
		(actionSubjectId: string, actions: CapacityPlanningAction[]) => {
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId,
				eventType: 'ui',
				attributes: {
					count: actions.length,
					viewType,
					actionTypeCounts: getActionTypeCounts(actions),
				},
			});
		},
		[sendAnalyticsEvent, viewType, getActionTypeCounts],
	);

	const runBulkAction = useCallback(
		async (
			actionsToRun: CapacityPlanningAction[],
			handler: (action: CapacityPlanningAction) => Promise<void>,
		) => {
			setIsBulkActioning(true);
			await Promise.all(actionsToRun.map((action) => handler(action)));
			setIsBulkActioning(false);
		},
		[],
	);

	const onAcceptAll = useCallback(() => {
		fireAnalytics('rovoCapacityPlanningAcceptAll', idleActions);
		return runBulkAction(idleActions, onConfirm);
	}, [fireAnalytics, runBulkAction, idleActions, onConfirm]);

	const onDismissAll = useCallback(() => {
		fireAnalytics('rovoCapacityPlanningDismissAll', idleActions);
		return runBulkAction(idleActions, onCancel);
	}, [fireAnalytics, runBulkAction, idleActions, onCancel]);

	const onAcceptSelected = useCallback(async () => {
		const actionsToRun = selectedActions.filter((action) => isActionIdle(action, getStatus));
		fireAnalytics('rovoCapacityPlanningAcceptSelected', actionsToRun);
		await runBulkAction(actionsToRun, onConfirm);
		onClearSelection();
	}, [fireAnalytics, runBulkAction, selectedActions, getStatus, onConfirm, onClearSelection]);

	const onDismissSelected = useCallback(async () => {
		const actionsToRun = selectedActions.filter((action) => isActionIdle(action, getStatus));
		fireAnalytics('rovoCapacityPlanningDismissSelected', actionsToRun);
		await runBulkAction(actionsToRun, onCancel);
		onClearSelection();
	}, [fireAnalytics, runBulkAction, selectedActions, getStatus, onCancel, onClearSelection]);

	if (allActionsApplied) {
		return null;
	}

	const isDisabled = idleActions.length === 0;

	return (
		<ActionCardFooter>
			{isBulkActioning ? (
				<Spinner size="small" interactionName="load" />
			) : selectedCount > 0 ? (
				<Flex xcss={footerStyles.wrapper}>
					<Button appearance="subtle" onClick={onDismissSelected} isDisabled={isDisabled}>
						{formatMessage(dismissSelectedButtonMessage)}
					</Button>
					<Button appearance="primary" onClick={onAcceptSelected} isDisabled={isDisabled}>
						{formatMessage(acceptSelectedButtonMessage)}
					</Button>
				</Flex>
			) : (
				<Flex xcss={footerStyles.wrapper}>
					<Button appearance="subtle" onClick={onDismissAll} isDisabled={isDisabled}>
						{formatMessage(dismissButtonMessage)}
					</Button>
					<Button appearance="primary" onClick={onAcceptAll} isDisabled={isDisabled}>
						{formatMessage(acceptAllButtonMessage)}
					</Button>
				</Flex>
			)}
		</ActionCardFooter>
	);
}
